import { Link } from "react-router";
import { Plus } from "lucide-react";
import type { SurveyListItem } from "@/lib/surveys/survey-service";
import { canEditSurveys } from "@/lib/surveys/survey-service";
import { describeAudience } from "@/lib/surveys/audience-presets";
import { formatAdminDate } from "@/lib/format/admin-time";
import { StatusChip } from "@/components/surveys/form";
import { useLoaderData } from "react-router";
import { load } from "../api/load";
import { useAdmin } from "../session";

export function loader() {
  return load<SurveyListItem[]>(`/api/v1/admin/surveys`);
}

export default function SurveysPage() {
  const surveys = useLoaderData() as SurveyListItem[];
  const auth = useAdmin();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-white tracking-tight">Surveys</h1>
          <p className="text-zinc-500 text-[13px] mt-0.5">In-app surveys shown on Home. Publish without an app release.</p>
        </div>
        <div className="flex-1" />
        {canEditSurveys(auth.role) && (
          <Link
            to="/admin/surveys/new"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-500 text-black text-[13px] font-semibold"
          >
            <Plus className="w-4 h-4" /> New survey
          </Link>
        )}
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
                {["Survey", "Status", "Questions", "Audience", "Shown", "Completed", "Updated"].map((h) => (
                  <th key={h} className="px-5 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.id} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-5 py-3">
                    <Link to={`/admin/surveys/${s.id}`} className="font-semibold text-white hover:text-emerald-400">
                      {s.title}
                    </Link>
                  </td>
                  <td className="px-5 py-3"><StatusChip status={s.status} /></td>
                  <td className="px-5 py-3 text-zinc-300">
                    {s.questionCount}
                    {s.feedbackEnabled ? " + feedback" : ""}
                  </td>
                  <td className="px-5 py-3 text-zinc-400">{describeAudience(s.audience).join(" · ")}</td>
                  <td className="px-5 py-3 text-zinc-300">{s.shown.toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3 text-zinc-300">
                    {s.completed.toLocaleString("en-IN")}
                    {s.shown > 0 && <span className="text-zinc-600"> ({Math.round((s.completed / s.shown) * 100)}%)</span>}
                  </td>
                  <td className="px-5 py-3 text-zinc-500">{formatAdminDate(s.updatedAt)}</td>
                </tr>
              ))}
              {surveys.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-zinc-500">
                    No surveys yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
